<template>
  <b-card no-body class="move-out-utility-card">
    <div class="d-flex justify-content-between align-items-start flex-wrap px-2 pt-2">
      <div class="d-flex align-items-center">
        <feather-icon icon="LogOutIcon" size="18" class="mr-50 text-primary" />
        <h5 class="mb-0">คำนวณค่าน้ำ-ค่าไฟ{{ mode === 'in' ? ' วันย้ายเข้า' : ' วันย้ายออก' }}</h5>
      </div>

      <b-button
        variant="outline-primary"
        size="sm"
        class="d-none d-md-inline-flex"
        :disabled="!canDownload || preparingPdf"
        :title="canDownload ? '' : 'กรอกเลขมิเตอร์อย่างน้อย 1 รายการก่อนดาวน์โหลด'"
        @click="downloadPdf"
      >
        <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
        {{ preparingPdf ? 'กำลังสร้างไฟล์...' : 'ดาวน์โหลด PDF' }}
      </b-button>
    </div>
    <small class="text-muted px-2 pb-1 d-block">
      ประมาณการค่าใช้จ่ายค้างชำระของผู้เช่า จากเลขมิเตอร์{{ mode === 'in' ? 'วันย้ายเข้าเทียบกับบิลถัดไป' : 'วันย้ายออกเทียบกับบิลล่าสุด' }}
    </small>

    <b-card-body class="pt-0">
      <b-row class="mb-1">
        <b-col md="6">
          <b-form-group class="mb-0">
            <label class="small mb-25">ชื่อผู้เช่า <span class="text-muted font-weight-normal">(ถ้ามี)</span></label>
            <b-form-input v-model="tenantName" size="sm" placeholder="เช่น คุณสมชาย ใจดี" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group class="mb-0">
            <label class="small mb-25">ห้อง / ยูนิต <span class="text-muted font-weight-normal">(ถ้ามี)</span></label>
            <b-form-input v-model="roomUnit" size="sm" placeholder="เช่น ตึก A ห้อง 12/34" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group class="mb-1">
        <label class="small mb-25 d-block">ประเภทการคำนวณ</label>
        <b-form-radio-group
          v-model="mode"
          :options="[
            { text: 'ย้ายเข้า (Move-In)', value: 'in' },
            { text: 'ย้ายออก (Move-Out)', value: 'out' },
          ]"
          buttons
          button-variant="outline-primary"
          size="sm"
        />
      </b-form-group>

      <b-row>
        <b-col md="6">
          <utility-calculator
            title="ค่าไฟฟ้า"
            title-en="Electricity"
            icon="ZapIcon"
            unit-label="หน่วย"
            variant="primary"
            :mode="mode"
            @summary="electricSummary = $event"
          />
        </b-col>

        <b-col md="6" class="move-out-utility-card__water">
          <utility-calculator
            title="ค่าน้ำ"
            title-en="Water"
            icon="DropletIcon"
            unit-label="หน่วย"
            variant="info"
            :default-rate="18"
            :mode="mode"
            @summary="waterSummary = $event"
          />
        </b-col>
      </b-row>

      <b-button
        variant="outline-primary"
        block
        class="d-md-none mt-1"
        :disabled="!canDownload || preparingPdf"
        :title="canDownload ? '' : 'กรอกเลขมิเตอร์อย่างน้อย 1 รายการก่อนดาวน์โหลด'"
        @click="downloadPdf"
      >
        <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
        {{ preparingPdf ? 'กำลังสร้างไฟล์...' : 'ดาวน์โหลด PDF' }}
      </b-button>
    </b-card-body>

    <!-- Hidden PDF content -->
    <div v-if="preparingPdf" ref="pdfContent" class="pdf-doc">
      <div class="pdf-header">
        <img
          src="https://digitalnation.sgp1.cdn.digitaloceanspaces.com/findbangkokroom/storage/1698858264911-picture.png"
          crossorigin="anonymous"
          class="pdf-logo"
        />
        <div class="pdf-company">
          <div class="pdf-company-name">FindBangkokRoom Co.,Ltd</div>
          <div>29 Soi Sukhumvit 18, Sukhumvit Road</div>
          <div>Klongtoey, Klongtoey, Bangkok 10110</div>
          <div>Tel. +66(0)-97-426-5147</div>
          <div>https://therealtors.net</div>
        </div>
      </div>

      <div class="pdf-title-block">
        <h4 class="pdf-title">{{ pdfTitle }}</h4>
        <table class="pdf-kv-table pdf-kv-table--tight">
          <tbody>
            <tr v-if="tenantName">
              <td>{{ bilingual('Tenant', 'ผู้เช่า') }}</td>
              <td>{{ tenantName }}</td>
            </tr>
            <tr v-if="roomUnit">
              <td>{{ bilingual('Room / Unit', 'ห้อง/ยูนิต') }}</td>
              <td>{{ roomUnit }}</td>
            </tr>
            <tr>
              <td>{{ bilingual('Document Date', 'วันที่ออกเอกสาร') }}</td>
              <td>{{ formatGregorian(new Date().toISOString().slice(0, 10)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-for="summary in pdfSummaries" :key="summary.title" class="pdf-section-spacing">
        <h5 class="pdf-section-title">{{ bilingual(summary.titleEn, summary.title) }}</h5>
        <table class="pdf-table">
          <tbody>
            <tr v-if="summary.billAmount && summary.billUnits">
              <td>{{ bilingual('Total Bill Amount / Units', 'ยอดบิลรวม / หน่วยรวมตามบิล') }}</td>
              <td>฿ {{ formatMoney(summary.billAmount) }} / {{ formatNumber(summary.billUnits) }} {{ bilingual(summary.unitLabelEn, summary.unitLabel) }}</td>
            </tr>
            <tr>
              <td>{{ bilingual('Average Rate per Unit', 'อัตราเฉลี่ยต่อหน่วย') }}</td>
              <td>฿ {{ formatRate(summary.rate) }} / {{ bilingual(summary.unitLabelEn, summary.unitLabel) }}</td>
            </tr>
            <tr v-if="summary.periodStartDate || summary.periodStartReading !== null">
              <td>{{ bilingualWithDate(summary.startReadingLabelEn, summary.startReadingLabel, summary.periodStartDate) }}</td>
              <td>{{ formatNumber(summary.periodStartReading) }}</td>
            </tr>
            <tr v-if="summary.periodEndDate || summary.periodEndReading !== null">
              <td>{{ bilingualWithDate(summary.endReadingLabelEn, summary.endReadingLabel, summary.periodEndDate) }}</td>
              <td>{{ formatNumber(summary.periodEndReading) }}</td>
            </tr>
            <tr>
              <td>{{ bilingual('Actual Units Used', 'จำนวนหน่วยที่ใช้จริง') }}</td>
              <td>{{ formatNumber(summary.unitsUsed) }} {{ bilingual(summary.unitLabelEn, summary.unitLabel) }}</td>
            </tr>
            <tr class="pdf-total-row">
              <td>{{ bilingual('Amount Due', 'ยอดที่ต้องชำระ') }}</td>
              <td>฿ {{ formatMoney(summary.amountDue) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pdf-section-spacing pdf-highlight-box">
        <div class="pdf-grand-total">
          <span>{{ bilingual('Total Utility Amount Due', 'ยอดรวมค่าน้ำ-ค่าไฟที่ต้องชำระ') }}</span>
          <strong>฿ {{ formatMoney(grandTotal) }}</strong>
        </div>
      </div>

      <p class="pdf-disclaimer">
        {{ bilingual(
          "This document is a preliminary estimate to help prepare payment before the move-out date. It is not an official invoice from the electricity or water authority. Please verify the actual figures with the building's juristic office.",
          'เอกสารนี้จัดทำเพื่อประมาณการเบื้องต้นสำหรับการเตรียมเงินก่อนวันย้ายออก มิใช่ใบแจ้งหนี้อย่างเป็นทางการจากการไฟฟ้า/การประปา กรุณายืนยันตัวเลขที่แท้จริงกับนิติบุคคลอาคารชุดอีกครั้ง'
        ) }}
      </p>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BFormRadioGroup,
} from 'bootstrap-vue'
import html2pdf from 'html2pdf.js'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import UtilityCalculator from './UtilityCalculator.vue'

export default {
  name: 'MoveOutUtilityCard',
  components: {
    BCard, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BFormRadioGroup, UtilityCalculator,
  },
  data() {
    return {
      mode: 'out',
      tenantName: null,
      roomUnit: null,
      preparingPdf: false,
      pdfSummaries: [],
      electricSummary: null,
      waterSummary: null,
    }
  },
  computed: {
    canDownload() {
      return Boolean(this.electricSummary?.hasResult || this.waterSummary?.hasResult)
    },
    grandTotal() {
      return this.pdfSummaries.reduce((sum, s) => sum + (s.amountDue || 0), 0)
    },
    pdfTitle() {
      return this.mode === 'in'
        ? this.bilingual('Move-In Utility Settlement', 'สรุปค่าน้ำ-ค่าไฟ วันย้ายเข้า')
        : this.bilingual('Move-Out Utility Settlement', 'สรุปค่าน้ำ-ค่าไฟ วันย้ายออก')
    },
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '-'
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatMoney(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '-'
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatRate(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return '-'
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })
    },
    formatGregorian(isoDate) {
      if (!isoDate) return ''
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(isoDate))
    },
    bilingual(en, th) {
      return `${en} (${th})`
    },
    bilingualWithDate(en, th, isoDate) {
      const datePart = isoDate ? ` ${this.formatGregorian(isoDate)}` : ''
      return `${en} (${th}${datePart})`
    },
    async downloadPdf() {
      this.pdfSummaries = [this.electricSummary, this.waterSummary].filter(s => s?.hasResult)

      if (!this.pdfSummaries.length) return

      this.preparingPdf = true
      await this.$nextTick()

      try {
        const element = this.$refs.pdfContent
        const opt = {
          margin: 0.35,
          filename: this.mode === 'in' ? 'move-in-utility-settlement.pdf' : 'move-out-utility-settlement.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }

        await html2pdf().from(element).set(opt).save()
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'สร้างไฟล์ PDF ไม่สำเร็จ',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        console.error('Error generating move-out utility PDF:', err)
      } finally {
        this.preparingPdf = false
      }
    },
  },
}
</script>

<style scoped>
.move-out-utility-card__water {
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}
.dark-layout .move-out-utility-card__water {
  border-left-color: rgba(255, 255, 255, 0.08);
}
@media (max-width: 767.98px) {
  .move-out-utility-card__water {
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding-top: 1.5rem;
    margin-top: 0.5rem;
  }
  .dark-layout .move-out-utility-card__water {
    border-top-color: rgba(255, 255, 255, 0.08);
  }
}

/* PDF */
.pdf-doc {
  padding: 20px;
  background: #fff;
  color: #2b2b2b;
  font-size: 13px;
}
.pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 2px solid #2b2b2b;
}
.pdf-logo {
  max-width: 200px;
  max-height: 85px;
}
.pdf-company {
  text-align: right;
  font-size: 12px;
  line-height: 1.4;
}
.pdf-company-name {
  font-weight: bold;
  font-size: 15px;
  margin-bottom: 2px;
}
.pdf-title-block {
  margin-bottom: 16px;
}
.pdf-title {
  margin: 0 0 10px;
}
.pdf-kv-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}
.pdf-kv-table--tight td {
  padding: 3px 0;
}
.pdf-kv-table td:first-child {
  color: #888;
  width: 160px;
}
.pdf-kv-table td:last-child {
  font-weight: 500;
}
.pdf-section-spacing {
  margin-bottom: 16px;
  page-break-inside: avoid;
}
.pdf-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5e6472;
  margin: 0 0 8px;
  padding-bottom: 5px;
  border-bottom: 1px solid #ccc;
}
.pdf-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}
.pdf-table td {
  padding: 5px 0;
}
.pdf-table td:first-child {
  color: #666;
}
.pdf-table td:last-child {
  text-align: right;
  font-weight: 500;
}
.pdf-total-row td {
  font-weight: bold;
  border-top: 1px solid #ccc;
  padding-top: 8px;
  color: #2b2b2b !important;
}
.pdf-highlight-box {
  background: #f4f8ec;
  border: 1px solid #cfe3a3;
  border-left: 4px solid #80ac03;
  border-radius: 6px;
  padding: 14px 16px;
}
.pdf-grand-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #5b7a1a;
}
.pdf-disclaimer {
  color: #888;
  font-size: 11px;
  margin-top: 12px;
}
</style>
